import { readStorage, writeStorage } from './safeStorage';

// Holds `<build> <reloads> <last reload ms>` for the life of the tab.
const RELOADS_KEY = 'applicant-ui:chunk-reloads';

// A chunk that fails again this soon after a reload fails for a reason a
// reload does not fix, so it reports instead of reloading.
const MIN_RELOAD_INTERVAL_MS = 60_000;

// Caps the reloads one build spends in a tab, so a page that fails more than
// a minute after every load cannot reload forever.
const MAX_RELOADS_PER_BUILD = 3;

const answeredErrors = new WeakSet<object>();

// Loads whose failure the page survives, such as a language switch's catalog.
let survivableLoads = 0;

/**
 * Runs `load` without a reload for a chunk that fails meanwhile. The caller
 * keeps the page usable when `load` rejects, so a reload would only blank it.
 */
export const withoutChunkReload = async <T>(load: () => Promise<T>) => {
  survivableLoads += 1;
  try {
    return await load();
  } finally {
    survivableLoads -= 1;
  }
};

/** Whether a reload, or the page being left, already answers this error. */
export const isReloadedChunkError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && answeredErrors.has(error);

const answer = (error: unknown): void => {
  if (typeof error === 'object' && error !== null) answeredErrors.add(error);
};

const reloadsSpent = (build: string, now: number): number | null => {
  const stored = readStorage('sessionStorage', RELOADS_KEY);
  if (stored === null) return 0;

  const [storedBuild, count, at] = stored.split(' ');
  if (storedBuild !== build) return 0;

  const spent = Number(count);
  const elapsed = now - Number(at);
  // An unreadable record spends nothing more: a loop is worse than a report.
  if (!Number.isInteger(spent) || !Number.isFinite(elapsed)) return null;

  return spent >= MAX_RELOADS_PER_BUILD || elapsed < MIN_RELOAD_INTERVAL_MS
    ? null
    : spent;
};

const recordReload = (build: string, reloads: number): boolean =>
  writeStorage(
    'sessionStorage',
    RELOADS_KEY,
    `${build} ${reloads} ${Date.now()}`,
  );

// Returns the tab's reload count after this one, or null when it spends none.
// Without storage nothing would stop a reload loop, so a failed write spends
// no reload.
const spendReload = (build: string): number | null => {
  const spent = reloadsSpent(build, Date.now());
  if (spent === null || !recordReload(build, spent + 1)) return null;
  return spent + 1;
};

/**
 * Reloads the page when a lazily loaded chunk fails to load.
 *
 * Vite dispatches `vite:preloadError` on window for every dynamic import that
 * fails, with the error as `payload`. A route chunk that fails otherwise
 * blanks the portal, because the root error boundary renders nothing, and a
 * reload fetches it again. The document's module map keeps a failed fetch, so
 * a later import of the same chunk in the same page fails without a request.
 *
 * A tab spends up to three reloads per build, at least a minute apart: a
 * candidate who keeps the portal open for hours gets a reload for each
 * passing network failure, and a chunk that fails right after a reload still
 * throws and reports. Every failure while a reload is pending, and every
 * import the browser cancels while the page is being left, is answered and
 * not reported. Offline, the reload waits for the network, because a reload
 * without one replaces the portal with the browser's offline page.
 */
export function reloadOnChunkLoadError(build: string): void {
  let reloadPending = false;
  let leaving = false;

  window.addEventListener('pagehide', () => {
    leaving = true;
  });
  window.addEventListener('pageshow', () => {
    leaving = false;
  });

  window.addEventListener('vite:preloadError', event => {
    const { payload } = event as Event & { payload?: unknown };

    if (leaving || reloadPending) {
      answer(payload);
      return;
    }
    if (survivableLoads > 0) return;
    const reloads = spendReload(build);
    if (reloads === null) return;

    answer(payload);
    reloadPending = true;
    if (navigator.onLine === false) {
      // The minute between reloads runs from the reload, not the failure.
      window.addEventListener(
        'online',
        () => {
          recordReload(build, reloads);
          window.location.reload();
        },
        { once: true },
      );
    } else {
      window.location.reload();
    }
  });
}
